import routes from '@Constants/Routes'
import { NumberFormatter } from '@Utilities/Formatters'
import { useSnackbar } from '@eduact/ed-components'
import { useAppDispatch, useAppSelector } from '@Hooks/ReduxHooks'
import { resetRunningTest } from '@Redux/RunningTestSlice/RunningTestSlice'
import { SnackbarConfig } from '@Utilities/SnackbarConfig'
import { playerBasePath } from '@Features/Course/Utilities/playerPaths'
import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useSubmitTestMutation } from '../Api/TestApi'
import { FormQuestion, useTest } from '../Providers/TestProvider'
import { isRetriable } from '../retryPolicy'
import useSaveAnswer from './useSaveAnswer'

/** Attempts per answer before giving up. Covers the common case: a brief blip. */
const MAX_ATTEMPTS = 3
/** Backoff between attempts, in ms. Kept short — a student is waiting on this. */
const RETRY_DELAY_MS = 600

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

type Failure = { question: FormQuestion; error: unknown }

/** A definite server refusal (4xx) carrying a message worth showing verbatim. */
const rejectionMessage = (error: unknown): string | undefined => {
	const status = (error as { status?: unknown })?.status
	const message = (error as { data?: { message?: unknown } })?.data?.message
	if (typeof status === 'number' && status >= 400 && status < 500 && typeof message === 'string' && message) return message
	return undefined
}

const useSubmitTest = () => {
	const { attempt, formQuestionsRef, flushPendingAnswers, settlePendingSaves } = useTest()
	const saveAnswer = useSaveAnswer()

	const [submitTest] = useSubmitTestMutation()
	const navigate = useNavigate()
	const { openSnackbar } = useSnackbar()
	const { testData } = useAppSelector(state => state.RunningTest)
	const dispatch = useAppDispatch()
	// `n` rather than `count`: `count` would put i18next into plural mode and
	// require the full Arabic plural set (zero/one/two/few/many/other). A missing
	// form there renders the raw key, which is not a thing to risk on the message
	// telling a student their answers did not save.
	const { t } = useTranslation(['test'])
	// A double click on Submit, or the timer firing while a click is in flight,
	// must not start a second concurrent finalize.
	const inFlight = useRef(false)

	return useCallback(
		/**
		 * @param withValidation refuse to submit while questions are unanswered
		 * @param strict         a deliberate submit by the student. False means the
		 *                       timer ran out, which changes what happens on failure.
		 */
		async (withValidation: boolean = true, strict: boolean = true) => {
			if (inFlight.current) return
			inFlight.current = true
			try {
				// Leave the caller's React context first: the timer calls this from a
				// passive effect, where React refuses flushSync. After one microtask
				// the flush below runs in a plain continuation and commits.
				await Promise.resolve()
				// Whatever is still sitting in an editor's debounce is applied to
				// state NOW (and its save started), then every save in flight —
				// including those — is awaited, so `answered` below is exact.
				flushPendingAnswers()
				await settlePendingSaves()
				// Never the closure copy: this callback may predate the flush.
				const questions = formQuestionsRef.current

				if (withValidation && questions.find(_ => _.answer === null)) {
					openSnackbar({ ...SnackbarConfig.warning, text: t('answer_all_to_submit') })
					return
				}

				// Only what the server has NOT already acknowledged.
				//
				// `submitted` is set exclusively after a successful save confirmed the
				// exact answer, so it means "the server confirmed this answer" — which
				// is what makes skipping safe. Re-sending confirmed answers is not
				// idempotent: `submitAnswer` DELETEs the stored row before re-resolving
				// the option BY TEXT, so re-sending an unchanged answer destroys it
				// whenever that option's text has since been edited.
				const answered = questions.filter(_ => _.answer !== null && !_.submitted)

				/** Send one answer, retrying a few times before admitting defeat. */
				const send = async (question: FormQuestion): Promise<Failure | null> => {
					let lastError: unknown = null
					for (let attemptNo = 1; attemptNo <= MAX_ATTEMPTS; attemptNo++) {
						const result = await saveAnswer(question, question.answer)
						// A request that succeeded but carried an answer the student has
						// since replaced is reported as NOT sent: finalizing would grade
						// the replaced answer, silently.
						if (result.ok) return result.confirmed ? null : { question, error: null }
						lastError = result.error
						// Only retry what a retry can fix. A 400 ('invalid choice') or a
						// 403 (attempt already closed) will fail identically every time.
						if (!isRetriable(result.error) || attemptNo === MAX_ATTEMPTS) break
						await wait(RETRY_DELAY_MS * attemptNo)
					}
					return { question, error: lastError }
				}

				// Concurrently: answers are independent rows keyed by question.
				const failures = (await Promise.all(answered.map(send))).filter((failure): failure is Failure => failure !== null)

				if (failures.length > 0) {
					// A definite refusal (e.g. "answer too long") is named as such, with
					// the server's own message; only genuine transport failures get the
					// "check your connection" text.
					const rejected = failures.find(failure => rejectionMessage(failure.error) !== undefined)
					const text = rejected
						? t('answer_rejected', {
								n: NumberFormatter(questions.findIndex(_ => _.id === rejected.question.id) + 1),
								message: rejectionMessage(rejected.error),
						  })
						: t(strict ? 'answers_not_saved' : 'answers_not_saved_time_up', { n: failures.length })
					openSnackbar({ ...SnackbarConfig.warning, text })
					// A deliberate submit with unsaved answers must NOT finalize.
					// Finalizing grades the attempt on whatever reached the server. When
					// time is up the server closes the attempt regardless, so refusing
					// would only strand the student on the page: say what happened.
					if (strict) return
				}

				try {
					await submitTest({
						attempt_id: attempt.id,
						course_id: testData?.course_id,
						unit_id: testData?.unit_id,
						strict,
					}).unwrap()
				} catch (error) {
					// Un-unwrapped, a failed finalize read as success and navigated away
					// from an attempt that is still open.
					openSnackbar({ ...SnackbarConfig.warning, text: t('submit_failed') })
					return
				}

				if (testData) {
					const path = `${playerBasePath(testData.parentType ?? 'classroom', testData.classroomLabel, testData.course_id)}/test/${testData.unit_id}`
					dispatch(resetRunningTest())
					navigate(path)
				} else {
					navigate(routes.home)
				}
			} finally {
				inFlight.current = false
			}
		},
		// `formQuestions` is deliberately NOT here: the callback reads the ref, so
		// its identity no longer changes on every answer (which re-fired the
		// timer's effect mid-submit and started a second concurrent finalize).
		[attempt, formQuestionsRef, flushPendingAnswers, settlePendingSaves, saveAnswer, testData, submitTest, openSnackbar, dispatch, navigate, t]
	)
}

export default useSubmitTest
